import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../layout/MainLayout";
import { Button1, Checkbox, Select, ToggleButton } from "../index";
import { FiPlus } from "react-icons/fi";
import CreateTransactionModal from "../modals/CreateTransactionModal";
import {
  BiSolidDollarCircle,
  BiSolidBank,
  BiSolidCreditCard,
  BiSolidShoppingBag,
  BiSolidPlane,
} from "react-icons/bi";
import { MdOutlineRestaurant, MdLocalHospital, MdSchool } from "react-icons/md";
import { useTransactions, useAccounts } from "../../data/live";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

const BRAND_GREEN = "#1BBB6B";
const RED = "#FC555B";
const LABEL_GRAY = "#7D7D7D";
const BORDER_GRAY = "#E3E3E3";

const SHORT_MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const buildMonthsStrip = (centerDate = new Date()) => {
  const arr = [];
  for (let i = -6; i < 6; i++) {
    const d = new Date(centerDate.getFullYear(), centerDate.getMonth() + i, 1);
    arr.push(`${SHORT_MONTHS[d.getMonth()]} - ${String(d.getFullYear()).slice(-2)}`);
  }
  return arr;
};

const parseMonthLabel = (label) => {
  const [m, y] = label.split(" - ");
  const monthIdx = SHORT_MONTHS.indexOf(m.toUpperCase());
  const year = 2000 + parseInt(y, 10);
  return { monthIdx, year };
};

const DOW_COLOR_FOR_CATEGORY = (cat) => {
  // Stable colour mapping for tx category labels
  const palette = ["#1A73E8", "#FFB800", "#FC555B", "#1BBB6B", "#9B59B6", "#FF6B35", "#00BCD4"];
  let hash = 0;
  for (let i = 0; i < (cat || "").length; i++) hash = (hash * 31 + cat.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const budgetedCategories = [
  { name: "Income", icon: <BiSolidDollarCircle />, color: BRAND_GREEN, checked: true },
];
const budgetedExpenses = [
  { name: "Total Income", icon: null, color: "", checked: false },
];
const actualIncome = [
  { name: "Income", icon: <BiSolidDollarCircle />, color: BRAND_GREEN, checked: true },
];
const actualExpenses = [
  { name: "Rent", icon: <BiSolidBank />, color: "#FC555B", amount: 0, checked: true },
  { name: "Groceries", icon: <BiSolidShoppingBag />, color: "#FFB800", amount: 0, checked: true },
  { name: "Education & Training", icon: <MdSchool />, color: "#FFB800", amount: 0, checked: false },
  { name: "Pets", icon: null, color: "", amount: 0, checked: false },
  { name: "Other & ATM", icon: null, color: "", amount: 0, checked: false },
  { name: "Mortgage & Loans", icon: null, color: "", amount: 0, checked: false },
  { name: "Travel", icon: <BiSolidPlane />, color: "", amount: 0, checked: false },
  { name: "Personal Care", icon: null, color: "", amount: 0, checked: false },
  { name: "Gift & Donation", icon: <BiSolidCreditCard />, color: "#1BBB6B", amount: 168, checked: true },
  { name: "Hobbies & Entertainment", icon: <MdOutlineRestaurant />, color: "#1A73E8", amount: 0, checked: false },
  { name: "Health", icon: <MdLocalHospital />, color: "#FC555B", amount: 0, checked: false },
];

const IncomeExpenseCalendar = () => {
  const { items: transactions, refresh: refreshTx } = useTransactions();
  const { accounts } = useAccounts();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  const todayDate = useMemo(() => new Date(), []);
  const defaultLabel = `${SHORT_MONTHS[todayDate.getMonth()]} - ${String(todayDate.getFullYear()).slice(-2)}`;
  const [selectedMonth, setSelectedMonth] = useState(defaultLabel);
  const monthsStrip = useMemo(() => buildMonthsStrip(todayDate), [todayDate]);

  const { monthIdx, year } = useMemo(
    () => parseMonthLabel(selectedMonth),
    [selectedMonth]
  );

  // First day of selected month (0=Sun..6=Sat) and total days in month
  const FIRST_DOW = useMemo(
    () => new Date(year, monthIdx, 1).getDay(),
    [year, monthIdx]
  );
  const daysInMonth = useMemo(
    () => new Date(year, monthIdx + 1, 0).getDate(),
    [year, monthIdx]
  );
  const daysInPrevMonth = useMemo(
    () => new Date(year, monthIdx, 0).getDate(),
    [year, monthIdx]
  );
  const activeMonth = useMemo(
    () => new Date(year, monthIdx, 1).toLocaleString("en-AU", { month: "long" }),
    [year, monthIdx]
  );

  /** entriesByDay built from real transactions inside the selected month */
  const entriesByDay = useMemo(() => {
    const map = {};
    for (const t of transactions) {
      const d = new Date(t.raw?.createdAt || t.date);
      if (d.getFullYear() !== year || d.getMonth() !== monthIdx) continue;
      const dayNum = d.getDate();
      const label =
        (t.category && t.category.length > 0
          ? t.category
          : t.type || "Tx").slice(0, 3) + "...";
      const isIncome = t.direction === "in";
      const entry = {
        type: isIncome ? "income" : "expense",
        label,
        amount: Number(t.amount || 0),
        color: isIncome ? BRAND_GREEN : DOW_COLOR_FOR_CATEGORY(t.category || t.type),
      };
      if (!map[dayNum]) map[dayNum] = [];
      map[dayNum].push(entry);
    }
    return map;
  }, [transactions, year, monthIdx]);

  /** Aggregate per-category totals for the side filter list */
  const expenseTotalsByCategory = useMemo(() => {
    const m = {};
    for (const t of transactions) {
      const d = new Date(t.raw?.createdAt || t.date);
      if (d.getFullYear() !== year || d.getMonth() !== monthIdx) continue;
      if (t.direction !== "out") continue;
      const cat = t.category || "Other";
      m[cat] = (m[cat] || 0) + Number(t.amount || 0);
    }
    return m;
  }, [transactions, year, monthIdx]);

  // Build 5-week (35-cell) grid
  const cells = useMemo(() => {
    const out = [];
    const startBackfill = FIRST_DOW;
    let prevDayCounter = daysInPrevMonth - startBackfill + 1;
    for (let i = 0; i < startBackfill; i++) {
      out.push({ day: prevDayCounter++, prev: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      out.push({ day: d, prev: false });
    }
    let nextCounter = 1;
    while (out.length < 42) {
      out.push({ day: nextCounter++, next: true });
    }
    // Trim to 5 or 6 rows depending on layout, keep 35 like original
    return out.slice(0, 35);
  }, [FIRST_DOW, daysInMonth, daysInPrevMonth]);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <MainLayout NavName="Manage My Spending" expand={false}>
      <div className="container-fluid px-4 py-3">
        {/* Month navigation */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <span className="fs-5 fw-semibold">{activeMonth}</span>
            <div
              className="d-flex gap-2 align-items-center"
              style={{ color: LABEL_GRAY }}
            >
              <span className="cursor-pointer" onClick={() => { const idx = monthsStrip.indexOf(selectedMonth); if (idx > 1) setSelectedMonth(monthsStrip[idx - 2]); }}>«</span>
              <span className="cursor-pointer" onClick={() => { const idx = monthsStrip.indexOf(selectedMonth); if (idx > 0) setSelectedMonth(monthsStrip[idx - 1]); }}>‹</span>
              <span className="cursor-pointer" onClick={() => { const idx = monthsStrip.indexOf(selectedMonth); if (idx < monthsStrip.length - 1) setSelectedMonth(monthsStrip[idx + 1]); }}>›</span>
              <span className="cursor-pointer" onClick={() => { const idx = monthsStrip.indexOf(selectedMonth); if (idx < monthsStrip.length - 2) setSelectedMonth(monthsStrip[idx + 2]); }}>»</span>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Select
              defaultValue="Start by"
              selectStyle={{ width: 120 }}
              options={[
                { name: "Budget", value: "budget" },
                { name: "Actual", value: "actual" },
              ]}
            />
            <Select
              defaultValue="End by"
              selectStyle={{ width: 120 }}
              options={[
                { name: "Today", value: "today" },
                { name: "End of month", value: "eom" },
              ]}
            />
          </div>
        </div>

        {/* Month strip */}
        <div
          className="d-flex gap-1 mb-3 overflow-auto pb-1"
          style={{ borderBottom: `1px solid ${BORDER_GRAY}` }}
        >
          {monthsStrip.map((m) => (
            <button
              key={m}
              className="btn btn-sm"
              onClick={() => setSelectedMonth(m)}
              style={{
                background: selectedMonth === m ? "#47E696" : "transparent",
                color: selectedMonth === m ? "#000" : LABEL_GRAY,
                borderRadius: "100px",
                fontSize: 13,
                padding: "6px 14px",
                border: "none",
              }}
            >
              {m}
            </button>
          ))}
        </div>

        {/* Account selector, driven by real wallets */}
        <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
          {accounts.length === 0 ? (
            <span className="fs14px" style={{ color: LABEL_GRAY }}>
              No wallets yet
            </span>
          ) : (
            accounts.slice(0, 4).map((a, idx) => (
              <Select
                key={a.id}
                defaultValue={`${a.currency}: ${Number(
                  a.availableBalance
                ).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                className="rounded-pill"
                selectStyle={{
                  width: 220,
                  background: idx === 0 ? "#E9FBF3" : undefined,
                }}
              />
            ))
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <span className="fs-5 fw-semibold">My Income & Expense</span>
          <div className="d-flex gap-2 align-items-center">
            <ToggleButton options={["List", "Calendar"]} defaultValue="Calendar" />
            <QuickActionSelect />
          </div>
        </div>

        <div className="row g-3">
          {/* Left sidebar: Filter (Budgeted + Actual) */}
          <div className="col-lg-3">
            <div
              className="rounded-3 p-3 h-100"
              style={{ border: `1px solid ${BORDER_GRAY}`, background: "#FFF" }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold">Filter</span>
              </div>

              <div className="mb-3">
                <div className="fw-medium mb-2" style={{ color: LABEL_GRAY }}>
                  Budgeted
                </div>
                {budgetedCategories.map((c) => (
                  <div
                    key={c.name}
                    className="d-flex align-items-center gap-2 py-1"
                  >
                    <Checkbox defaultChecked={c.checked} />
                    <span style={{ color: c.color }}>{c.icon}</span>
                    <span className="fs14px">{c.name}</span>
                  </div>
                ))}
                {budgetedExpenses.map((c) => (
                  <div
                    key={c.name}
                    className="d-flex align-items-center gap-2 py-1 ps-3"
                  >
                    <Checkbox defaultChecked={c.checked} />
                    <span className="fs12px" style={{ color: LABEL_GRAY }}>
                      {c.name}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <div className="fw-medium mb-2" style={{ color: LABEL_GRAY }}>
                  Actual
                </div>
                {actualIncome.map((c) => (
                  <div
                    key={c.name}
                    className="d-flex align-items-center gap-2 py-1"
                  >
                    <Checkbox defaultChecked={c.checked} />
                    <span style={{ color: c.color }}>{c.icon}</span>
                    <span className="fs14px">{c.name}</span>
                  </div>
                ))}
              </div>

              <div>
                <div className="fw-medium mb-2" style={{ color: LABEL_GRAY }}>
                  Expenses ({activeMonth})
                </div>
                {actualExpenses.map((c) => {
                  const realTotal = expenseTotalsByCategory[c.name] || 0;
                  return (
                    <div
                      key={c.name}
                      className="d-flex align-items-center justify-content-between py-1"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Checkbox defaultChecked={realTotal > 0 || c.checked} />
                        <span style={{ color: c.color }}>{c.icon}</span>
                        <span className="fs14px">{c.name}</span>
                      </div>
                      <span className="fs12px fw-medium">
                        {realTotal > 0
                          ? `$${realTotal.toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}`
                          : "$0"}
                      </span>
                    </div>
                  );
                })}
                {Object.keys(expenseTotalsByCategory)
                  .filter(
                    (k) =>
                      !actualExpenses.find((c) => c.name === k) &&
                      expenseTotalsByCategory[k] > 0
                  )
                  .map((cat) => (
                    <div
                      key={cat}
                      className="d-flex align-items-center justify-content-between py-1"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Checkbox defaultChecked />
                        <span style={{ color: DOW_COLOR_FOR_CATEGORY(cat) }}>•</span>
                        <span className="fs14px">{cat}</span>
                      </div>
                      <span className="fs12px fw-medium">
                        ${expenseTotalsByCategory[cat].toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="col-lg-9">
            <div
              className="rounded-3 overflow-hidden"
              style={{ border: `1px solid ${BORDER_GRAY}`, background: "#FFF" }}
            >
              {/* Day headers */}
              <div
                className="d-grid"
                style={{
                  gridTemplateColumns: "repeat(7, 1fr)",
                  background: "#F8F9FB",
                  borderBottom: `1px solid ${BORDER_GRAY}`,
                }}
              >
                {dayNames.map((d) => (
                  <div
                    key={d}
                    className="p-2 text-center fw-medium"
                    style={{ color: LABEL_GRAY, fontSize: 13 }}
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Cells */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gridAutoRows: "minmax(110px, auto)",
                }}
              >
                {cells.map((c, i) => {
                  const entries = c.prev || c.next ? [] : entriesByDay[c.day] || [];
                  const isOutside = c.prev || c.next;
                  const total =
                    entries.length > 0
                      ? entries.reduce(
                          (s, e) => s + (e.type === "income" ? e.amount : -e.amount),
                          0
                        )
                      : 0;
                  return (
                    <div
                      key={i}
                      className="p-2"
                      style={{
                        borderRight: `1px solid ${BORDER_GRAY}`,
                        borderBottom: `1px solid ${BORDER_GRAY}`,
                        background: isOutside ? "#F8F9FB" : "#FFF",
                        minHeight: "110px",
                        position: "relative",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span
                          className="fs12px fw-medium"
                          style={{ color: isOutside ? "#BEBEBE" : "#161616" }}
                        >
                          {c.day}
                        </span>
                        <span
                          className="fs12px"
                          style={{ color: total >= 0 ? BRAND_GREEN : RED }}
                        >
                          {total !== 0
                            ? `${total >= 0 ? "+" : ""}$${Math.abs(total)}`
                            : "$0"}
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {entries.map((e, idx) => (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center px-2 py-1 rounded"
                            style={{
                              background: e.color,
                              color: "#FFF",
                              fontSize: 11,
                            }}
                          >
                            <span>{e.label}</span>
                            <span>${e.amount}</span>
                          </div>
                        ))}
                        {!isOutside && (
                          <div
                            className="d-flex align-items-center gap-1 cursor-pointer mt-1"
                            style={{ color: BRAND_GREEN, fontSize: 11 }}
                            onClick={() => setShowCreateModal(true)}
                          >
                            <FiPlus size={12} />
                            <span>Add New</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3 gap-2">
              <Button1 buttonName="Back" className="border" onClick={() => navigate(-1)} />
              <Button1
                buttonName="Add Transaction"
                className="btn-dark"
                onClick={() => setShowCreateModal(true)}
              />
            </div>
          </div>
        </div>
      </div>

      <CreateTransactionModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={() => {
          refreshTx();
          setShowCreateModal(false);
        }}
      />
    </MainLayout>
  );
};

export default IncomeExpenseCalendar;
